import classNames from 'classnames';
import { Children, ForwardedRef, forwardRef, ReactNode, useCallback } from 'react';
import { Grid, GridProps, List } from 'yoso';
import { usePlaceholder } from '~/components/Grid/Placeholder/Placeholder.hooks';
import styles from './PlaceholderGrid.module.scss';

type Props = {
  placeholder?: ReactNode;
};
export type PlaceholderGridProps<T extends HTMLElement = HTMLOListElement> = Props &
  Omit<GridProps<T>, keyof Props>;
export const PlaceholderGrid = forwardRef(function PlaceholderGrid<
  T extends HTMLElement = HTMLOListElement,
>(
  { className, children, placeholder, as = List, ...props }: PlaceholderGridProps,
  ref: ForwardedRef<T>
) {
  const { ref: placeholderRef, length } = usePlaceholder(Children.count(children));
  const listRef = useCallback(
    (el: T | null) => {
      placeholderRef(el);

      if (typeof ref === 'function') {
        ref(el);
      } else if (ref !== null) {
        ref.current = el;
      }
    },
    [placeholderRef, ref]
  );

  return (
    <Grid className={classNames(styles.root, className)} as={as} {...props} ref={listRef}>
      {children}
      {placeholder && Array.from({ length }).map((_, i) => <li key={i}>{placeholder}</li>)}
    </Grid>
  );
});
